import React from 'react';
import { Link } from 'react-router-dom';

import { linkColumn } from '../../components/BaseTable/BaseTable';
import PlatformCell from '../../components/cells/PlatformCell/PlatformCell';
import FAIcon from '../../components/FAIcon/FAIcon';
import { digitalCodesConstants } from '../../constants/digitalCodesConstants';
import { platformFeatures } from '../../constants/platformConstants';
import { formatCurrency } from '../../utils/currencyUtils';
import { dateFormat, formatDate, parseDateString } from '../../utils/dateUtils';
import { displayStringForStatus, variantForDigitalCodesStatus } from '../../utils/digitalCodesUtils';
import { getPlatformCodeCustomEvaluation } from '../../utils/filterUtils';
import { displayString } from '../../utils/stringUtils';
import { isFeatureActiveForPlatform } from '../../utils/userUtils';


const formatDateField = (value) => {
	return formatDate(parseDateString(value), dateFormat.DATE);
};

const {
	DRAFT, READY_FOR_REVIEW, READY_FOR_APPROVAL, REJECTED, CANCELED, AWAITING_PAYMENT_INFO,
	PAYMENT_PENDING, PAYMENT_RECEIVED, UPLOAD_IN_PROGRESS, UPLOAD_FAILED, CODES_AVAILABLE,
	DOWNLOAD_EXPIRED, COMPLETE
} = digitalCodesConstants.status;

const READY_FOR_ACTIVATION = 'Ready For Activation';
const ACTIVE = 'Active';
const INACTIVE = 'Inactive';

export const getFiltersConfig = ({
	userProfile,
	isReviewer,
	isApprover,
	isActivator,
	isCreditUser,
	purposesData,
	platforms,
}) => {
	const filterProperties = new Map();

	const usablePlatforms = platforms.filter(({ platform_code }) =>
		isFeatureActiveForPlatform(
			userProfile,
			platformFeatures.DIGITAL_CODES,
			platform_code,
			'view',
		),
	);

	if (usablePlatforms.length > 1) {
		filterProperties.set('Platform', {
			filter: 'platform_code',
			selectableFilters: new Set(usablePlatforms.map(({ platform_name }) => platform_name)),
			customEvaluation: getPlatformCodeCustomEvaluation(platforms),
		});
	}

	const commonStatuses = [
		DRAFT,
		READY_FOR_REVIEW,
		READY_FOR_APPROVAL,
		REJECTED,
		CANCELED,
		AWAITING_PAYMENT_INFO,
		PAYMENT_PENDING,
		PAYMENT_RECEIVED,
		CODES_AVAILABLE,
		DOWNLOAD_EXPIRED,
		COMPLETE,
	];
	const internalOnlyStatuses = [UPLOAD_IN_PROGRESS, UPLOAD_FAILED];

	const selectableStatus =
		isApprover || isReviewer ? [...commonStatuses, ...internalOnlyStatuses] : commonStatuses;

	const defaultSelectedStatus = isApprover
		? [READY_FOR_APPROVAL, CODES_AVAILABLE, COMPLETE]
		: isReviewer
			? [
				READY_FOR_REVIEW,
				PAYMENT_PENDING,
				PAYMENT_RECEIVED,
				UPLOAD_IN_PROGRESS,
				UPLOAD_FAILED,
				CODES_AVAILABLE,
				COMPLETE,
		  ]
			: [
				DRAFT,
				READY_FOR_REVIEW,
				READY_FOR_APPROVAL,
				AWAITING_PAYMENT_INFO,
				PAYMENT_PENDING,
				PAYMENT_RECEIVED,
				CODES_AVAILABLE,
		  ];

	filterProperties.set('Status', {
		filter: 'status',
		selectedFilters: new Set(
			defaultSelectedStatus.map((status) => displayStringForStatus(status)),
		),
		selectableFilters: new Set(
			selectableStatus.map((status) => displayStringForStatus(status)),
		),
		customEvaluation: (status, filters) => filters.has(displayStringForStatus(status)),
	});

	filterProperties.set('Purpose Type', {
		filter: 'purpose_type',
		selectableFilters: new Set(
			Object.keys(digitalCodesConstants.purposeTypes).map((v) => displayString(v)),
		),
	});

	if (isActivator) {
		filterProperties.set('Code Status', {
			filter: 'code_activation',
			selectableFilters: new Set([READY_FOR_ACTIVATION, ACTIVE, INACTIVE]),
		});
	}

	return filterProperties;
};

export const getTableFormat = ({ canCreateOrder, canViewCompanies, canViewAllOrderDetails, canViewCreditHoldOrders, availablePlatforms, users }) => {
	const DateCell = ({ value }) => {
		if (!value) { return null; }
		return <span>{formatDateField(value)}</span>;
	};

	const StatusCell = ({ value }) => {
		return (
			<div>
				<strong className={`text-${variantForDigitalCodesStatus(value)}`}>
                    {displayStringForStatus(value).toUpperCase()}
				</strong>
			</div>
		);
	};
	return [
		{
			Header: 'Publisher',
			accessor: 'company_name',
			show: canViewCompanies || canViewCreditHoldOrders,
		},
		{ 
			Header: 'Platform',
			accessor: 'platform_code',
			Cell: PlatformCell,
			show: canViewAllOrderDetails,
		},
		{
			Header: 'Purpose Type',
			accessor: 'purpose_type',
			Cell: ({ value }) => displayString(value),
			show: canViewAllOrderDetails,
		},
		{
			Header: 'Publisher PO',
			accessor: 'publisherPO',
		},
		{
			Header: 'Sales Order',
			accessor: 'ebsSalesOrderNum',
			show: canViewCompanies,
		},
		{
			Header: 'Date Submitted',
			accessor: 'submittedDate',
			Cell: DateCell,
		},
		{
			Header: 'Total Price',
			id: 'grand_total',
			accessor: (row) => Number(row.grand_total),
			Cell: function PriceCell({ value }) {
				if (!value) { return null; }
				return <span>{formatCurrency(value)}</span>;
			},
		},
		{
			Header: 'Status',
			accessor: 'status',
			Cell: StatusCell,
		},
		{
			...linkColumn,
			Cell: function LinkCell({ original }) {
				if (
					original.status === digitalCodesConstants.status.DRAFT &&
					canCreateOrder &&
					availablePlatforms?.includes(original.platform_code) &&
					original.sales_flow_version !== 1
				) {
					return (
						<Link to={`/orders/digital-codes/${original.orderID}/create`}>
							<FAIcon name="chevron-right" />
						</Link>
					);
				}
				return (
					<Link to={`/orders/digital-codes/${original.orderID}`}>
						<FAIcon name="chevron-right" />
					</Link>
				);
			},
			show: canViewAllOrderDetails,
		},
		{
			Header: 'Email',
			accessor: 'submitted_by_ndid',
			width: 10,
			Cell: ({ value, original }) => {
				const user = users.find((u) => u.ndid_user_id === value);
				return (
					<>
						{user ? (
							<a
								href={`mailto:${user.email_address}?subject=${encodeURIComponent(
									`Questions regarding payment for NOA order ${original.publisherPO || ''}`
								)}`}
								title={`Email ${user.email_address}`}
							>
								<FAIcon name="envelope" />
							</a>
						) : null}
					</>
				);
			},
			show: canViewCreditHoldOrders,
		},
	];
};

export const getActivationStatus = ({ activatable, activation_date }) => {
	if (activation_date)
		return ACTIVE;
	if (activatable)
		return READY_FOR_ACTIVATION;
	return INACTIVE;
};
